import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Divider,
    Grid
} from '@mui/material';
import {
    CheckCircle,
    Error,
    Factory as FactoryIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Business as BusinessIcon
} from '@mui/icons-material';

// -------------------------- IMPORTS Y TIPOS --------------------------
import { usePostApiPlants } from '@/api/gen/api';
// import StandaloneRolePermissionManager from '@/components/StandAlonePermissionManager';

// Tipos basados en tu API
interface RolePermission {
    role: string;
    permissionLevel: 'READ' | 'WRITE' | 'ADMIN';
}

interface PlantData {
    plantName: string;
    clientPhoneContact: string;
    clientEmail: string;
    clientId: number;
    rolePermissions: RolePermission[];
}

interface ClientData {
    id: number;
    clientName: string;
    clientPhoneContact: string;
    clientEmail: string;
}

interface StepPlantRegisterClientProps {
    onNext: (data: PlantData, apiResponse: any) => void;
    clientData: ClientData; // Datos del cliente creado en el paso anterior
}

// -------------------------- COMPONENTE PRINCIPAL --------------------------
const StepPlantRegisterClient: React.FC<StepPlantRegisterClientProps> = ({
    onNext,
    clientData
}) => {
    // -------------------------- ESTADOS --------------------------
    const [formData, setFormData] = useState<PlantData>({
        plantName: '',
        clientPhoneContact: clientData.clientPhoneContact || '',
        clientEmail: clientData.clientEmail || '',
        clientId: clientData.id,
        rolePermissions: [{ role: 'ADMIN', permissionLevel: 'ADMIN' }]
    });

    const [errors, setErrors] = useState<Partial<Record<keyof PlantData, string>>>({});

    // -------------------------- HOOKS --------------------------
    const {
        mutate: createPlant,
        isLoading,
        isSuccess,
        data: apiResponse
    } = usePostApiPlants();

    // -------------------------- MANEJADORES --------------------------
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (validateForm() && !isLoading) {
            createPlant({ data: formData });
        }
    };

    const handleContinueToNextStep = () => {
        if (apiResponse?.success) {
            onNext(formData, apiResponse);
        }
    };

    const handleInputChange = (field: keyof PlantData) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Limpiar error del campo
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    // const handleRolesChange = (rolePermissions: RolePermission[]) => {
    //     setFormData(prev => ({
    //         ...prev,
    //         rolePermissions
    //     }));
    // };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof PlantData, string>> = {};

        if (!formData.plantName.trim()) {
            newErrors.plantName = 'El nombre de la planta es requerido';
        }

        if (!formData.clientEmail.trim()) {
            newErrors.clientEmail = 'El email es requerido';
        } else if (!/\S+@\S+\.\S+/.test(formData.clientEmail)) {
            newErrors.clientEmail = 'El formato del email no es válido';
        }

        if (!formData.clientPhoneContact.trim()) {
            newErrors.clientPhoneContact = 'El teléfono de contacto es requerido';
        }

        // Validar roles
        const hasEmptyRoles = formData.rolePermissions.some(perm => !perm.role.trim());
        if (hasEmptyRoles) {
            newErrors.rolePermissions = 'Todos los roles deben tener un nombre';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // -------------------------- RENDERIZADO BASADO EN RESPUESTA REAL --------------------------

    // CASO 1: Éxito real (success: true)
    if (isSuccess && apiResponse?.success === true) {
        return (
            <Box sx={{ mt: 2 }}>
                <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
                    Planta creada exitosamente
                </Alert>

                <Typography variant="h5" gutterBottom color="primary">
                    Paso 2 Completado: Planta Creada
                </Typography>

                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'success.main' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="success.main">
                            Planta Creada - ID: {apiResponse.id}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Nombre de la Planta:</Typography>
                                <Typography variant="body1">{formData.plantName}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Cliente Asociado:</Typography>
                                <Typography variant="body1">{clientData.clientName} (ID: {clientData.id})</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Estado:</Typography>
                                <Typography variant="body1" color="success.main">Creada Exitosamente</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleContinueToNextStep}
                        startIcon={<CheckCircle />}
                    >
                        Continuar al Siguiente Paso
                    </Button>
                </Box>
            </Box>
        );
    }

    // CASO 2: Éxito técnico pero fallo de negocio (success: false)
    if (isSuccess && apiResponse?.success === false) {
        return (
            <Box sx={{ mt: 2 }}>
                <Alert severity="error" icon={<Error />} sx={{ mb: 3 }}>
                    ❌ Error al crear la planta
                </Alert>

                <Typography variant="h5" gutterBottom color="error">
                    Paso 2 Fallido
                </Typography>

                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'error.main' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="error.main">
                            Error en la creación
                        </Typography>

                        {apiResponse.errors && apiResponse.errors.map((apiError: any, index: number) => (
                            <Alert
                                key={index}
                                severity="warning"
                                sx={{ mb: 1 }}
                            >
                                <Typography variant="subtitle2">
                                    {apiError.description}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Ubicación: {apiError.location}
                                </Typography>
                            </Alert>
                        ))}

                        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                            <strong>Datos enviados:</strong>
                        </Typography>
                        <Box sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                            <pre style={{ fontSize: '0.75rem', margin: 0 }}>
                                {JSON.stringify(formData, null, 2)}
                            </pre>
                        </Box>
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={() => window.location.reload()}
                    >
                        Volver a intentar
                    </Button>
                </Box>
            </Box>
        );
    }

    // -------------------------- RENDERIZADO DEL FORMULARIO --------------------------

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mt: 1, mb: 3 }}>
                Complete la información de la planta. Los campos con * son obligatorios.
            </Alert>

            {/* Título */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <FactoryIcon color='primary' sx={{ fontSize: 32 }} />
                <Typography variant='h4' component='h1' fontWeight='bold'>
                    Crear Nueva Planta
                </Typography>
            </Box>

            {/* PRIMERA FILA: Cliente Asociado  */}
            <Card sx={{ 
                boxShadow: 3, 
                borderRadius: 2,
                transition: 'box-shadow 0.3s ease-in-out',
                '&:hover': {
                    boxShadow: 6,
                },
                backgroundColor: 'action.hover',
                mb: 3
            }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <BusinessIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                            Cliente Asociado
                        </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={3}>
                        {/* Nombre del Cliente*/}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Cliente"
                                value={clientData.clientName}
                                disabled
                                helperText="Cliente creado en el paso anterior"
                            />
                        </Grid>

                        {/* ID del Cliente  */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="ID del Cliente"
                                value={clientData.id}
                                disabled
                                helperText="ID automático del cliente"
                            />
                        </Grid>
                    </Grid>

                    <Alert severity="info" sx={{ mt: 2 }}>
                        Esta planta será asociada automáticamente al cliente <strong>{clientData.clientName}</strong> creado en el paso anterior.
                    </Alert>
                </CardContent>
            </Card>

            {/* SEGUNDA FILA: Información de Planta y Contacto */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Información de la Planta */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        height: 'fit-content', 
                        boxShadow: 3, 
                        borderRadius: 2,
                        transition: 'box-shadow 0.3s ease-in-out',
                        '&:hover': {
                            boxShadow: 6,
                        },
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FactoryIcon color='primary' />
                                <Typography variant='h6' fontWeight='bold'>
                                    Información de la Planta
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <TextField
                                fullWidth
                                label="Nombre de la Planta *"
                                value={formData.plantName}
                                onChange={handleInputChange('plantName')}
                                error={!!errors.plantName}
                                helperText={errors.plantName || 'Ingrese el nombre identificativo de la planta'}
                                disabled={isLoading}
                                required
                                sx={{ mb: 2 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* Información de Contacto */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        height: 'fit-content', 
                        boxShadow: 3, 
                        borderRadius: 2,
                        transition: 'box-shadow 0.3s ease-in-out',
                        '&:hover': {
                            boxShadow: 6,
                        },
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <PhoneIcon color='primary' />
                                <Typography variant='h6' fontWeight='bold'>
                                    Información de Contacto
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <TextField
                                fullWidth
                                label="Teléfono de Contacto *"
                                value={formData.clientPhoneContact}
                                onChange={handleInputChange('clientPhoneContact')}
                                error={!!errors.clientPhoneContact}
                                helperText={errors.clientPhoneContact || 'Número de teléfono para contacto'}
                                disabled={isLoading}
                                required
                                InputProps={{
                                    startAdornment: (
                                        <PhoneIcon sx={{ color: 'action.active', mr: 1, ml: 0.5 }} />
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label="Email de Contacto *"
                                type="email"
                                value={formData.clientEmail}
                                onChange={handleInputChange('clientEmail')}
                                error={!!errors.clientEmail}
                                helperText={errors.clientEmail || 'Dirección de email para contacto'}
                                disabled={isLoading}
                                required
                                InputProps={{
                                    startAdornment: (
                                        <EmailIcon sx={{ color: 'action.active', mr: 1, ml: 0.5 }} />
                                    ),
                                }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {errors.rolePermissions && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.rolePermissions}
                </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    * Campos obligatorios
                </Typography>
            </Box>

            {/* Botón de envío */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                    sx={{ borderRadius: 2 }}
                >
                    {isLoading ? 'Creando Planta...' : 'Crear Planta y Continuar'}
                </Button>
            </Box>
        </Box>
    );
};

export default StepPlantRegisterClient;